import {
  DownloadIcon,
  FileTextIcon,
  ListBulletIcon,
} from '@radix-ui/react-icons';
import { Box, Button, Flex, Grid, GridProps, Skeleton } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import {
  MlbLogoIcon,
  RowsAddIcon,
} from 'components/common/custom-icon/shorthands';
import { ErrorBoundary } from 'components/common/error-boundary';
import { NoDataPlaceholder } from 'components/common/table/no-data-placeholder';
import { SectionHeader } from 'components/sections/header';
import { CsvUploadDialog } from 'components/sections/hitter-library/dialogs/csv-upload';
import { HitterLibraryDuplicateCallout } from 'components/sections/hitter-library/duplicate-callout';
import { HitterLibraryFilters } from 'components/sections/hitter-library/filters';
import { HitterLibraryFooter } from 'components/sections/hitter-library/footer';
import { HitterLibraryHitterCard } from 'components/sections/hitter-library/hitter/card';
import { HittersContext } from 'contexts/hitters.context';
import { CheckAllButton } from 'contexts/layout/checked-context/select-all-button';
import { CheckedProvider } from 'contexts/layout/checked.context';
import { CrudAction } from 'enums/tables';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { t } from 'i18next';
import { ITableNoData } from 'interfaces/tables/no-data';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { HitterSafety, HitterSide } from 'lib_ts/enums/hitters.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IHitter, IHitterUpsert } from 'lib_ts/interfaces/i-hitter';
import {
  PropsWithChildren,
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { VirtuosoGrid, VirtuosoGridProps } from 'react-virtuoso';
import { AdminTeamsService } from 'services/admin/teams.service';
import { MainService } from 'services/main.service';
import { useAuthStore } from 'stores/use-auth-store';

const OVERSCAN_PIXELS = 900;

const LibraryGrid = forwardRef(
  ({ children, style }: PropsWithChildren<GridProps>, ref: React.Ref<any>) => (
    <Grid
      ref={ref}
      columns={{ initial: '1', sm: '2', lg: '3' }}
      gap="3"
      style={style}
    >
      {children}
    </Grid>
  )
);

LibraryGrid.displayName = 'LibraryGrid';

const gridComponents: VirtuosoGridProps<IHitter, unknown>['components'] = {
  List: LibraryGrid,
};

export const HitterLibrary = () => {
  const current = useAuthStore((s) => s.current);
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);
  const team = useAuthStore((s) => s.team);

  const {
    hitters,
    filtered,
    loading,
    setFilters,
    openCrudDialog,
    setLastChanged,
  } = useContext(HittersContext);

  // reset filters on unmount
  useEffect(() => {
    return () => {
      setFilters({});
    };
  }, []);

  const filteredIDs = useMemo(() => filtered.map((m) => m._id), [filtered]);

  const noDataProps: ITableNoData = {
    noDataHeader: 'No Hitters to Display',
    noDataBody: 'No hitters match your search',
  };

  const dummyPlayers = useMemo(
    () => Array.from({ length: 24 }, (_, i) => i),
    []
  );

  const [dialogCsv, setDialogCsv] = useState<number>();

  const playerAppEnabled = useFeatureFlag('player-app');

  return (
    <ErrorBoundary identifier="HitterLibrary">
      <Flex direction="column" gap="3" style={{ height: '100%' }}>
        <SectionHeader
          header={t('main.hitters')}
          secondaryAction={
            <Button
              variant={RADIX.BUTTON.VARIANT.SECONDARY}
              onClick={async () => {
                const partials: IHitterUpsert[] = hitters.map((h) => {
                  // coalesce to guarantee the CSV column exists
                  const o: IHitterUpsert = {
                    _id: h._id,
                    customID: h.customID ?? '',
                    name: h.name ?? '',
                    level: h.level ?? '',
                    height_ft: h.height_ft ?? -1,
                    side: h.side ?? HitterSide.RHH,
                    safety_buffer: h.safety_buffer ?? HitterSafety.low,
                    source: h.source,
                    // phones link hitters to players, which needs the player app
                    ...(playerAppEnabled && { phone: h._mx_phone ?? '' }),
                  };

                  return o;
                });

                const csvString =
                  await MainService.getInstance().convertJSONToCSV(partials);
                const blob = new Blob([csvString], { type: 'text/csv' });

                MiscHelper.saveAs(blob, `hitters-library.csv`);
              }}
            >
              <DownloadIcon /> {t('common.export-library')}
            </Button>
          }
          actionsMenuTrigger={
            <Button>
              <RowsAddIcon /> {t('common.add-or-update-hitters')}
            </Button>
          }
          actions={[
            {
              group: '_1',
              prefixIcon: <MlbLogoIcon />,
              label: 'MLB Stats API',
              invisible:
                // only allowed for admins and team admins
                !userRoleFeatureFlags.adminTeams.statsApiHitterImport ||
                // only allowed for teams that are allowed to use Stats API
                !current.mlb_stats_api ||
                // only allowed for teams w/ an orgID configured
                !team?.stats_api_orgId,
              description: team?.enable_hitters_import
                ? 'This feature is already enabled for your organization'
                : "Automatically update your hitter library with your organization's rosters",
              disabled:
                loading ||
                // hitter import is already enabled
                !!team?.enable_hitters_import,
              onClick: async () => {
                if (!team) {
                  return;
                }

                const updated =
                  await AdminTeamsService.getInstance().updateTeam({
                    _id: team._id,
                    enable_hitters_import: true,
                  });

                if (updated) {
                  useAuthStore.getState().setTeam(updated);

                  NotifyHelper.success({
                    message_md:
                      'Hitter sync via MLB Stats API has been enabled.',
                  });

                  // start a first import for the user's team
                  const importSuccess =
                    await AdminTeamsService.getInstance().importStatsApiHitters(
                      team._id
                    );

                  if (importSuccess) {
                    NotifyHelper.success({
                      message_md:
                        'An initial import has completed successfully. Refreshing hitters...',
                    });

                    setLastChanged(Date.now());
                  }
                }
              },
            },
            {
              group: '_2',
              prefixIcon: <FileTextIcon />,
              label: 'CSV Upload',
              description: 'Upload a CSV file with multiple hitters',
              disabled: loading,
              onClick: () => setDialogCsv(Date.now()),
            },
            {
              group: '_3',
              prefixIcon: <ListBulletIcon />,
              label: 'Manual',
              description: 'Fill a form to create a hitter',
              disabled: loading,
              onClick: () =>
                openCrudDialog({
                  key: Date.now(),
                  action: CrudAction.Create,
                  models: [],
                }),
            },
          ]}
          extendedActionsMenu
        />

        <CheckedProvider data={filtered}>
          <Flex
            direction="column"
            gap="3"
            style={{
              height: '100%',
            }}
          >
            <Flex gap={RADIX.FLEX.GAP.SM}>
              <CheckAllButton ids={filteredIDs} />
              <HitterLibraryFilters />
            </Flex>

            <HitterLibraryDuplicateCallout />

            {!loading && filtered.length === 0 && (
              <NoDataPlaceholder {...noDataProps} />
            )}

            <Box height="100%" overflowY="auto">
              {loading ? (
                <LibraryGrid>
                  {dummyPlayers.map((_, i) => (
                    <Skeleton key={i} height="80px" />
                  ))}
                </LibraryGrid>
              ) : (
                <VirtuosoGrid
                  data={filtered}
                  totalCount={filtered.length}
                  overscan={OVERSCAN_PIXELS}
                  components={gridComponents}
                  itemContent={(_, h) => (
                    <HitterLibraryHitterCard key={h._id} hitter={h} />
                  )}
                />
              )}
            </Box>

            <HitterLibraryFooter hitters={filtered} />
          </Flex>
        </CheckedProvider>
      </Flex>

      {dialogCsv && (
        <CsvUploadDialog
          key={dialogCsv}
          onClose={() => setDialogCsv(undefined)}
        />
      )}
    </ErrorBoundary>
  );
};
